import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { tokenStore } from "@/lib/api";
import { profileStore } from "@/lib/profileStore";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | authenticated | anonymous

  const fetchMe = useCallback(async () => {
    if (!tokenStore.get()) {
      setUser(null);
      setTenant(null);
      setStatus("anonymous");
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setTenant(data.tenant || null);
      setStatus("authenticated");
      return data;
    } catch (_e) {
      tokenStore.clear();
      setUser(null);
      setTenant(null);
      setStatus("anonymous");
      return null;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    profileStore.clear(); // force profile picker after every fresh login
    tokenStore.set(data.access_token);
    setUser(data.user);
    setStatus("authenticated");
    // refresh /me to populate tenant info
    await fetchMe();
    return data;
  }, [fetchMe]);

  const signup = useCallback(async ({ email, password, full_name, tenant_slug }) => {
    const { data } = await api.post("/auth/signup", {
      email,
      password,
      full_name,
      tenant_slug,
    });
    profileStore.clear();
    tokenStore.set(data.access_token);
    setUser(data.user);
    setStatus("authenticated");
    await fetchMe();
    return data;
  }, [fetchMe]);

  const logout = useCallback(() => {
    tokenStore.clear();
    profileStore.clear();
    setUser(null);
    setTenant(null);
    setStatus("anonymous");
  }, []);

  const value = useMemo(
    () => ({ user, tenant, status, login, signup, logout, refresh: fetchMe }),
    [user, tenant, status, login, signup, logout, fetchMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
